<template>
  <UsageExample v-model="tab" :code="code" :tabs="tabs">
    <div class="wrapper">
      <div class="style-04 pb-4">Demo Wrapper</div>

      <div class="d-flex flex-column ga-8">
        <div class="d-flex flex-column">
          <div class="style-04 pb-4">Interactive Demo</div>
          <HxContentCard
            :height="height"
            :color="cardColor"
            :elevation="cardElevation"
            :min-height="minHeight"
            :max-height="maxHeight"
            :width="width"
            :min-width="minWidth"
            :max-width="maxWidth"
          >
            <template v-if="showCardTitle" #header>
              <div class="font-weight-bold">Interactive Card</div>
              <div class="text-subtitle-2">Customizable with controls</div>
            </template>
            <template v-if="showCardContent" #default>
              <div :class="{ 'text-justify': justifyText }">
                {{ cardContent }}
              </div>
            </template>
            <template v-if="showAction" #footer>
              <HxSearch :search="search" />
              <HxButton
                id="buttonId"
                size="large"
                :label="buttonLabel"
                variant="primary"
                right-icon="mdi-plus-circle-outline"
                class="ml-auto"
              />
            </template>
          </HxContentCard>
        </div>

        <div class="d-flex flex-column">
          <div class="style-04 pb-4">Elevation Variants</div>
          <div class="d-flex flex-wrap ga-4">
            <HxContentCard elevation="flat" class="flex-grow-1" style="min-width: 300px">
              <template #header>
                <div class="font-weight-bold">Flat Elevation</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
            <HxContentCard elevation="md" class="flex-grow-1" style="min-width: 300px">
              <template #header>
                <div class="font-weight-bold">MD Elevation</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
            <HxContentCard elevation="xl" class="flex-grow-1" style="min-width: 300px">
              <template #header>
                <div class="font-weight-bold">XL Elevation</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
          </div>
        </div>

        <div class="d-flex flex-column">
          <div class="style-04 pb-4">Color Variants</div>
          <div class="d-flex flex-wrap ga-4">
            <HxContentCard color="white" class="flex-grow-1" style="min-width: 300px">
              <template #header>
                <div class="font-weight-bold">White Color</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
            <HxContentCard color="grey" class="flex-grow-1" style="min-width: 300px">
              <template #header>
                <div class="font-weight-bold">Grey Color</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
          </div>
        </div>

        <div class="d-flex flex-column">
          <div class="style-04 pb-4">Content Layouts</div>
          <div class="d-flex flex-wrap ga-4">
            <HxContentCard class="flex-grow-1" style="min-width: 300px">
              <template #header>
                <div class="font-weight-bold">With Header</div>
                <div class="text-subtitle-2">And Subtitle</div>
              </template>
              <div>{{ shortContent }}</div>
              <template #footer>
                <HxButton label="Action" variant="primary" />
              </template>
            </HxContentCard>
            <HxContentCard class="flex-grow-1" style="min-width: 300px">
              <div>{{ shortContent }}</div>
            </HxContentCard>
          </div>
        </div>

        <div class="d-flex flex-column">
          <div class="style-04 pb-4">Width Variants</div>
          <div class="d-flex flex-column ga-4">
            <HxContentCard>
              <template #header>
                <div class="font-weight-bold">Full Width</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
            <HxContentCard width="300px">
              <template #header>
                <div class="font-weight-bold">Fixed Width (300px)</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>

            <HxContentCard height="30vh">
              <template #header>
                <div class="font-weight-bold">Fixed Height (30vh)</div>
              </template>
              <div>{{ shortContent }}</div>
            </HxContentCard>
          </div>
        </div>

        <div class="d-flex flex-column">
          <div class="style-04 pb-4">Overflow Content (Height 200px)</div>
          <div class="d-flex flex-wrap ga-4">
            <HxContentCard height="200px">
              <template #header>
                <div class="font-weight-bold">Scrollable Content</div>
              </template>
              <div>
                {{ longContent }}
                <hx-chip label="Helix Chip" />
              </div>
            </HxContentCard>
          </div>
        </div>
      </div>
    </div>
    <template #configuration>
      <div class="d-flex flex-column mt-16">
        <v-switch
          v-model="showGrey"
          :label="`Color: ${cardColor.toString()}`"
          color="primary"
          @change="toggleGrey"
        ></v-switch>
        <v-switch
          v-model="showAction"
          :label="`Show Action: ${showAction.toString()}`"
          color="primary"
        ></v-switch>
        <v-switch
          v-model="showCardTitle"
          :label="`Show Title: ${showCardTitle.toString()}`"
          color="primary"
        ></v-switch>
        <v-switch
          v-model="showCardContent"
          :label="`Show Content: ${showCardContent.toString()}`"
          color="primary"
        ></v-switch>
        <v-switch
          v-model="justifyText"
          :label="`Justify Text: ${justifyText.toString()}`"
          color="primary"
        ></v-switch>

        <v-select
          v-model="cardElevation"
          label="Elevation"
          :items="['flat', 'md', 'xl']"
          max-width="200"
          class="mt-4"
          color="primary"
          variant="outlined"
        ></v-select>
        <v-select
          v-model="contentPreset"
          label="Content Length"
          :items="contentPresets"
          max-width="200"
          class="mt-4"
          color="primary"
          variant="outlined"
        ></v-select>
      </div>
    </template>
  </UsageExample>
</template>

<script>
import UsageExample from '@/components/helix/utils/UsageExample';

export default {
  name: 'HelixContentCard',
  components: { UsageExample },
  setup() {
    const code = `
// Basic Usage
<HxContentCard>
  <div>Basic card content</div>
</HxContentCard>

// With Header and Subtitle
<HxContentCard>
  <template #header>
    <div class="font-weight-bold">Card Title</div>
    <div class="text-subtitle-2">Subtitle</div>
  </template>
  <div>Card content</div>
</HxContentCard>

// With Footer and Actions
<HxContentCard>
  <div>Card content</div>
  <template #footer>
    <HxButton label="Action" variant="primary" />
  </template>
</HxContentCard>

// Interactive Card with All Props
<HxContentCard
  :height="height"
  :color="cardColor"
  :elevation="cardElevation"
  :min-height="minHeight"
  :max-height="maxHeight"
  :width="width"
  :min-width="minWidth"
  :max-width="maxWidth"
>
  <template #header>
    <div class="font-weight-bold">Card Title</div>
    <div class="text-subtitle-2">Subtitle</div>
  </template>
  <div :class="{ 'text-justify': justifyText }">
    {{ cardContent }}
  </div>
  <template #footer>
    <HxSearch :search="search" />
    <HxButton
      :label="buttonLabel"
      variant="primary"
      right-icon="mdi-plus-circle-outline"
      class="ml-auto"
    />
  </template>
</HxContentCard>

// Color Variants
<HxContentCard color="white">
  <div>White card</div>
</HxContentCard>

<HxContentCard color="grey">
  <div>Grey card</div>
</HxContentCard>

// Elevation Variants
<HxContentCard elevation="flat">
  <div>Flat elevation</div>
</HxContentCard>

<HxContentCard elevation="md">
  <div>Medium elevation</div>
</HxContentCard>

<HxContentCard elevation="xl">
  <div>Extra large elevation</div>
</HxContentCard>

// Width and Height Variants
<HxContentCard width="100%">
  <div>Full width card</div>
</HxContentCard>

<HxContentCard width="300px">
  <div>Fixed width card</div>
</HxContentCard>

<HxContentCard height="30vh">
  <div>Fixed height card</div>
</HxContentCard>

// Scrollable Content
<HxContentCard height="200px">
  <div class="w-100">
    {{ longContent }}
    <hx-chip label="Helix Chip" />
  </div>
</HxContentCard>`;

    return {
      code
    };
  },
  data() {
    return {
      tab: 'Content Card Usage',
      tabs: ['Content Card Usage'],
      cardTitle: 'Helix Card Title',
      longContent:
        'The Content Engine (CE) is Jae Testing 3rd of December a decoupled Content Management System (CMS) developed for the primary purpose of delivering writen contextualised content at scale. The CE core technology manages content analogous to how Github managed code. Authors write their content in MS Word documents abiding by the protocols of the CE Authoring Methodology (CEAM). The content is imported into the CE where they are converted into MarkDown files.\n\n' +
        'The Content Engine (CE) is Jae Testing 3rd of December a decoupled Content Management System (CMS) developed for the primary purpose of delivering writen contextualised content at scale. The CE core technology manages content analogous to how Github managed code. Authors write their content in MS Word documents abiding by the protocols of the CE Authoring Methodology (CEAM). The content is imported into the CE where they are converted into MarkDown files.\n\n' +
        'The Content Engine (CE) is Jae Testing 3rd of December a decoupled Content Management System (CMS) developed for the primary purpose of delivering writen contextualised content at scale. The CE core technology manages content analogous to how Github managed code. Authors write their content in MS Word documents abiding by the protocols of the CE Authoring Methodology (CEAM). The content is imported into the CE where they are converted into MarkDown files.',
      shortContent:
        'This is a short example of card content that demonstrates how the card looks with minimal text.',
      cardElevation: 'flat',
      cardVariant: 'outlined',
      showElevation: false,
      showGrey: false,
      showCardTitle: true,
      showCardContent: true,
      justifyText: false,
      cardColor: 'white',
      showAction: true,
      height: undefined,
      minHeight: undefined,
      maxHeight: undefined,
      width: undefined,
      minWidth: undefined,
      maxWidth: undefined,
      buttonLabel: 'Button',
      contentPreset: 'long',
      contentPresets: [
        { title: 'Short Content', value: 'short' },
        { title: 'Long Content', value: 'long' }
      ],
      search: ''
    };
  },
  computed: {
    cardContent() {
      return this.contentPreset === 'short' ? this.shortContent : this.longContent;
    }
  },
  methods: {
    toggleGrey() {
      if (this.showGrey) {
        this.cardColor = 'grey';
      } else {
        this.cardColor = 'white';
      }
    }
  }
};
</script>

<style scoped>
.wrapper {
  background-color: #f4f5f6;
  padding: 16px;
  width: 100%;
  max-width: 100%;
}
</style>
